import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, MatOption } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import moment, { Moment } from 'moment';
import { Observable } from 'rxjs';
import {
  AmlCheckFilter,
  AmlDashboardApplication,
  AmlDashboardEntity,
  AmlDashboardFilters,
  AmlDashboardIndividual,
  AmlDashboardSort,
  AmlDashboardSortProp,
  AmlOfficerCandidate,
  AmlOfficerFilterValue,
  AmlKybFilter,
  AmlKycFilter,
  AmlPrivacyFilter,
  AmlRiskFilter,
  AML_CHECK_OPTIONS,
  AML_DASHBOARD_CSV_MAX_ROWS,
  AML_DEFAULT_CHECK,
  AML_DEFAULT_STAGES,
  AML_KYB_OPTIONS,
  AML_KYC_OPTIONS,
  AML_OFFICER_FILTER_ME,
  AML_OFFICER_FILTER_UNASSIGNED,
  AML_PRIVACY_OPTIONS,
  AML_RISK_OPTIONS,
  AML_STAGE_OPTIONS,
  StageNameType,
  amlRiskRank,
  canSetAmlOfficer,
  KycStatus,
  KybResultStatus,
  kybDisplayStatus,
  privacyConsentDisplayStatus,
} from '@portal-workspace/grow-shared-library';
import {
  CustomContentLoaderComponent,
  CustomPaginatorComponent,
  getUser,
  PortalHotToastService,
} from '@portal-workspace/grow-ui-library';
import { ApplicationService } from '../../../service/application.service';
import { DigitalIdService } from '../../../service/digital-id.service';
import { navigationUrlForApplication } from '../../../service/navigation-urls';
import { AmlMultiFilterComponent } from './aml-multi-filter.component';

/** Applications before this were handled outside the AML queue. */
const DEFAULT_CREATED_FROM = '2026-09-07';
const defaultStartDate = (): Moment => moment(DEFAULT_CREATED_FROM, 'YYYY-MM-DD');
const DEFAULT_SORT: AmlDashboardSort = { prop: 'applicationId', dir: 'desc' };

const sameItems = <T>(a: T[], b: T[]): boolean =>
  a.length === b.length && a.every(item => b.includes(item));

/** A null icon renders as a muted dash, the KYC tab's "nothing yet" marker. */
interface CheckIcon {
  icon: string | null;
  styleClass: string;
  label: string;
}

const NOT_STARTED: CheckIcon = { icon: null, styleClass: 'not-available', label: 'Not started' };

// Same icons and tooltips as the application's KYC tab.
const KYC_ICONS: Partial<Record<KycStatus, CheckIcon>> = {
  RECEIVED_COMPLETE: { icon: 'mdi-check-circle-outline', styleClass: 'ok', label: 'Verification successful' },
  RECEIVED_PENDING_REVIEW: { icon: 'mdi-progress-alert', styleClass: 'pending-review', label: 'Under review' },
  RECEIVED_INCOMPLETE: { icon: 'mdi-progress-question', styleClass: 'incomplete', label: 'Verification incomplete' },
  RECEIVED_FAILED: { icon: 'mdi-alert-circle-outline', styleClass: 'error', label: 'KYC verification failed' },
  RECEIVED_ERROR: { icon: 'mdi-alert-circle-outline', styleClass: 'error', label: 'System error' },
  SMS_SENT: { icon: 'mdi-timer-sand', styleClass: 'waiting', label: 'Verification SMS sent' },
};

const PRIVACY_FAILED_STYLES = ['dispatchfailed', 'declined', 'expired'];

@Component({
  selector: 'aml-list',
  templateUrl: './aml-list.component.html',
  styleUrls: ['./aml-list.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  imports: [
    CommonModule,
    MatTableModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatMenuModule,
    MatSortModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    FormsModule,
    NgClass,
    RouterLink,
    CustomContentLoaderComponent,
    CustomPaginatorComponent,
    AmlMultiFilterComponent,
  ],
  providers: [
    DigitalIdService,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
})
export class AmlListComponent implements OnInit {

  displayedColumns: string[] = [
    'applicationId',
    'stage',
    'created',
    'officer',
    'borrowingEntity',
    'privacy',
    'kyc',
    'kyb',
    'risk',
    'expand',
  ];

  dataSource: AmlDashboardApplication[] = [];
  total = 0;
  limit = 20;
  offset = 0;
  expandedApplicationId: number | null = null;
  isLoading = false;
  isExporting = false;
  candidates: AmlOfficerCandidate[] = [];
  officerSearchText = '';

  /** Empty matches every officer. */
  officerFilter: AmlOfficerFilterValue[] = [];
  checksFilter: AmlCheckFilter = AML_DEFAULT_CHECK;
  /** Empty matches every stage. */
  stageFilter: StageNameType[] = [...AML_DEFAULT_STAGES];
  /** Empty adds no condition. */
  privacyFilter: AmlPrivacyFilter[] = [];
  kycFilter: AmlKycFilter[] = [];
  kybFilter: AmlKybFilter[] = [];
  riskFilter: AmlRiskFilter[] = [];
  searchFilter = '';
  startDate: Moment | null = defaultStartDate();
  endDate: Moment | null = null;
  sort: AmlDashboardSort = DEFAULT_SORT;

  readonly officerMe = AML_OFFICER_FILTER_ME;
  readonly officerUnassigned = AML_OFFICER_FILTER_UNASSIGNED;
  readonly checksOptions = AML_CHECK_OPTIONS;
  readonly privacyOptions = AML_PRIVACY_OPTIONS;
  readonly kycOptions = AML_KYC_OPTIONS;
  readonly kybOptions = AML_KYB_OPTIONS;
  readonly riskOptions = AML_RISK_OPTIONS;
  readonly stageOptions = AML_STAGE_OPTIONS;

  readonly loggedInUser = getUser();
  readonly canEditOfficer = canSetAmlOfficer(this.loggedInUser);
  private busyActions = new Set<string>();

  constructor(private applicationService: ApplicationService,
              private digitalIdService: DigitalIdService,
              private toastService: PortalHotToastService,
              private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.loadCandidates();
    this.loadData();
  }

  private currentFilters(): AmlDashboardFilters {
    return {
      officers: this.officerFilter,
      checks: this.checksFilter,
      stages: this.stageFilter,
      privacy: this.privacyFilter,
      kyc: this.kycFilter,
      kyb: this.kybFilter,
      risks: this.riskFilter,
      search: this.searchFilter.trim() || undefined,
      // CreateTime is UTC, so the picked local day is sent as the UTC instants that bracket it.
      startDate: this.startDate ? moment(this.startDate).startOf('day').toISOString() : undefined,
      endDate: this.endDate ? moment(this.endDate).endOf('day').toISOString() : undefined,
    };
  }

  private loadCandidates(): void {
    this.applicationService.getAmlOfficerCandidatesFn().subscribe({
      next: (candidates) => {
        this.candidates = candidates;
        this.cdr.markForCheck();
      },
      error: () => {
        this.toastService.error('Unable to load AML officers', 'Error');
      },
    });
  }

  loadData(): void {
    this.isLoading = true;
    this.applicationService.getAmlDashboardFn({
      page: { limit: this.limit, offset: this.offset },
      sorts: [this.sort],
      filters: this.currentFilters(),
    }).subscribe({
      next: (response) => {
        this.dataSource = response.payload;
        this.total = response.total;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.toastService.error('Unable to load the AML list', 'Error');
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onPagination(event: PageEvent): void {
    this.offset = event.pageIndex;
    this.limit = event.pageSize;
    this.expandedApplicationId = null;
    this.loadData();
  }

  onSortChange(sort: Sort): void {
    this.sort = sort.direction
      ? { prop: sort.active as AmlDashboardSortProp, dir: sort.direction }
      : DEFAULT_SORT;
    this.applyFilters();
  }

  applyFilters(): void {
    this.offset = 0;
    this.expandedApplicationId = null;
    this.loadData();
  }

  clearFilters(): void {
    this.officerFilter = [];
    this.checksFilter = AML_DEFAULT_CHECK;
    this.stageFilter = [...AML_DEFAULT_STAGES];
    this.privacyFilter = [];
    this.kycFilter = [];
    this.kybFilter = [];
    this.riskFilter = [];
    this.searchFilter = '';
    this.startDate = defaultStartDate();
    this.endDate = null;
    this.applyFilters();
  }

  hasFilters(): boolean {
    return this.officerFilter.length > 0
      || this.checksFilter !== AML_DEFAULT_CHECK
      || !sameItems(this.stageFilter, AML_DEFAULT_STAGES)
      || this.privacyFilter.length > 0
      || this.kycFilter.length > 0
      || this.kybFilter.length > 0
      || this.riskFilter.length > 0
      || this.searchFilter.trim() !== ''
      || !(this.startDate && this.startDate.isSame(defaultStartDate(), 'day'))
      || this.endDate !== null;
  }

  exportCsv(): void {
    this.isExporting = true;
    this.applicationService.downloadAmlDashboardCsv(this.currentFilters(), this.sort).pipe(
      this.toastService.spinnerObservable(),
    ).subscribe({
      next: ({ blob, truncated }) => {
        this.isExporting = false;
        this.saveBlob(blob, `aml-dashboard-${moment().format('YYYY-MM-DD')}.csv`);
        if (truncated) {
          this.toastService.warn(
            `Only the first ${AML_DASHBOARD_CSV_MAX_ROWS} rows were exported. Narrow the filters to export the rest.`,
            'Export truncated');
        } else {
          this.toastService.success('AML list exported', 'Success');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isExporting = false;
        this.toastService.error('Unable to export the AML list', 'Error');
        this.cdr.markForCheck();
      },
    });
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  toggleExpanded(element: AmlDashboardApplication): void {
    this.expandedApplicationId = this.expandedApplicationId === element.applicationId
      ? null
      : element.applicationId;
  }

  isExpanded(element: AmlDashboardApplication): boolean {
    return this.expandedApplicationId === element.applicationId;
  }

  detailId(element: AmlDashboardApplication): string {
    return `aml-detail-${element.applicationId}`;
  }

  applicationLink(element: AmlDashboardApplication): string[] {
    return navigationUrlForApplication(String(element.applicationId), 'kyc-aml');
  }

  candidateName(candidate: AmlOfficerCandidate): string {
    return [candidate.GivenName, candidate.FamilyName].filter(Boolean).join(' ') || candidate.Email;
  }

  filteredCandidates(): AmlOfficerCandidate[] {
    const search = this.officerSearchText.trim().toLowerCase();
    if (!search) {
      return this.candidates;
    }
    return this.candidates.filter(candidate =>
      this.candidateName(candidate).toLowerCase().includes(search)
      || candidate.Email.toLowerCase().includes(search));
  }

  /**
   * The search text is shared by every officer select, so only the open one is filtered. A closed
   * select that lost its selected option would show nothing.
   */
  candidateOptions(select: MatSelect): AmlOfficerCandidate[] {
    return select.panelOpen ? this.filteredCandidates() : this.candidates;
  }

  onOfficerPanelToggle(open: boolean, select: MatSelect, searchInput: HTMLInputElement): void {
    this.officerSearchText = '';
    if (open) {
      setTimeout(() => {
        searchInput.focus();
        this.scrollOptionIntoView(select.selected);
      });
    }
  }

  /**
   * The search box sits in the select's overlay, outside the select's own keydown listener, so list
   * navigation keys are handed back to it. Every other key stays in the box as typing.
   */
  onOfficerSearchKeydown(event: KeyboardEvent, select: MatSelect): void {
    if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].includes(event.key)) {
      select._handleKeydown(event);
      this.scrollOptionIntoView(select._keyManager?.activeItem);
    } else {
      event.stopPropagation();
    }
  }

  // The options scroll inside their own box under the search field, so the select's built-in
  // scrolling (which moves the whole panel) no longer reaches them.
  private scrollOptionIntoView(option: MatOption | MatOption[] | null | undefined): void {
    if (option && !Array.isArray(option)) {
      option._getHostElement().scrollIntoView({ block: 'nearest' });
    }
  }

  /**
   * A multi-select drops selected values whose options the search has hidden, so the choices made
   * among the visible options are merged with the hidden ones already selected.
   */
  onOfficerFilterChange(visibleSelection: AmlOfficerFilterValue[]): void {
    const visible = new Set<AmlOfficerFilterValue>([
      AML_OFFICER_FILTER_ME,
      AML_OFFICER_FILTER_UNASSIGNED,
      ...this.filteredCandidates().map(candidate => candidate.UserId),
    ]);
    this.officerFilter = [...this.officerFilter.filter(value => !visible.has(value)), ...visibleSelection];
  }

  officerFilterLabel(): string {
    const names = [
      ...(this.officerFilter.includes(AML_OFFICER_FILTER_ME) ? ['Me'] : []),
      ...(this.officerFilter.includes(AML_OFFICER_FILTER_UNASSIGNED) ? ['Unassigned'] : []),
      ...this.officerFilter
        .filter((value): value is number => typeof value === 'number')
        .map(userId => this.officerName(userId) ?? `User ${userId}`),
    ];
    return names.length <= 2 ? names.join(', ') : `${names.length} officers`;
  }

  stageFilterLabel(): string {
    if (this.stageFilter.length === 0 || this.stageFilter.length === AML_STAGE_OPTIONS.length) {
      return 'All';
    }
    if (sameItems(this.stageFilter, AML_DEFAULT_STAGES)) {
      return 'Open';
    }
    return this.stageFilter.length === 1 ? this.stageFilter[0] : `${this.stageFilter.length} stages`;
  }

  selectOpenStages(): void {
    this.stageFilter = [...AML_DEFAULT_STAGES];
  }

  selectAllStages(): void {
    this.stageFilter = [...AML_STAGE_OPTIONS];
  }

  unselectAllStages(): void {
    this.stageFilter = [];
  }

  selectAllOfficers(): void {
    this.officerFilter = [AML_OFFICER_FILTER_ME, AML_OFFICER_FILTER_UNASSIGNED, ...this.candidates.map(candidate => candidate.UserId)];
  }

  unselectAllOfficers(): void {
    this.officerFilter = [];
  }

  /** An officer who has since lost the ops access level is no longer a candidate but still shown. */
  isOfficerOutsideCandidates(element: AmlDashboardApplication): boolean {
    return element.amlOfficerUserId != null
      && !this.candidates.some(candidate => candidate.UserId === element.amlOfficerUserId);
  }

  onOfficerChange(element: AmlDashboardApplication, amlOfficerUserId: number | null): void {
    if (!this.canEditOfficer || amlOfficerUserId === element.amlOfficerUserId) {
      return;
    }
    const key = this.officerKey(element);
    this.busyActions.add(key);
    this.applicationService.updateAmlOfficerFn({ applicationId: element.applicationId, amlOfficerUserId }).pipe(
      this.toastService.spinnerObservable(),
    ).subscribe({
      next: (response) => {
        this.busyActions.delete(key);
        this.toastService.success(response.message || 'AML officer updated', 'Success');
        // Updated in place rather than reloaded, so the row stays put until the next refresh even
        // when the new officer no longer matches the officer filter.
        this.dataSource = this.dataSource.map(row => row.applicationId === element.applicationId
          ? {
            ...row,
            amlOfficerUserId,
            amlOfficerName: this.officerName(amlOfficerUserId),
            amlOfficerAssignedAt: new Date().toISOString(),
          }
          : row);
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.busyActions.delete(key);
        this.toastService.error(error?.error?.message || 'Unable to update the AML officer', 'Error');
        this.loadData();
      },
    });
  }

  private officerName(userId: number | null): string | null {
    const candidate = this.candidates.find(c => c.UserId === userId);
    return candidate ? this.candidateName(candidate) : null;
  }

  isOfficerBusy(element: AmlDashboardApplication): boolean {
    return this.busyActions.has(this.officerKey(element));
  }

  private officerKey(element: AmlDashboardApplication): string {
    return `officer-${element.applicationId}`;
  }

  canResendPrivacy(individual: AmlDashboardIndividual): boolean {
    return !individual.privacyCompleted;
  }

  canResendKycSms(individual: AmlDashboardIndividual): boolean {
    return !individual.kycCompleted;
  }

  /** Re-evaluation re-scores an existing result, so it needs a completed KYB run to work from. */
  canReevaluateKyb(entity: AmlDashboardEntity): boolean {
    return entity.latestKybRunId != null && entity.kybStatus === 'COMPLETED';
  }

  isBusy(key: string): boolean {
    return this.busyActions.has(key);
  }

  privacyKey(element: AmlDashboardApplication, individual: AmlDashboardIndividual): string {
    return `privacy-${element.applicationId}-${individual.applicationIndividualId}`;
  }

  kycKey(element: AmlDashboardApplication, individual: AmlDashboardIndividual): string {
    return `kyc-${element.applicationId}-${individual.applicationIndividualId}`;
  }

  kybKey(entity: AmlDashboardEntity): string {
    return `kyb-${entity.applicationCommercialEntityId}`;
  }

  resendPrivacy(element: AmlDashboardApplication, individual: AmlDashboardIndividual): void {
    this.runAction(
      this.privacyKey(element, individual),
      this.digitalIdService.reissueConsentForm(element.applicationId, individual.individualId),
      (response) => {
        const result = response.payload;
        if (result.dispatchFailed || !result.dispatched) {
          this.toastService.error(response.message || 'Privacy consent was not sent', 'Error');
        } else {
          this.toastService.success(`Privacy consent sent to ${individual.name}`, 'Success');
        }
      },
      'Unable to send the privacy consent',
    );
  }

  resendKycSms(element: AmlDashboardApplication, individual: AmlDashboardIndividual): void {
    this.runAction(
      this.kycKey(element, individual),
      this.digitalIdService.sendVerificationSmsFn(element.applicationId, individual.individualId),
      () => this.toastService.success(`KYC SMS sent to ${individual.name}`, 'Success'),
      'Unable to send the KYC SMS',
    );
  }

  reevaluateKyb(entity: AmlDashboardEntity): void {
    if (!this.canReevaluateKyb(entity) || entity.latestKybRunId == null) {
      return;
    }
    this.runAction(
      this.kybKey(entity),
      this.applicationService.retryKybReevaluationFn(entity.latestKybRunId),
      (response) => this.toastService.success(response.message || 'Risk re-evaluation sent', 'Success'),
      'Unable to send the risk re-evaluation',
    );
  }

  private runAction<T>(key: string, action$: Observable<T>, onSuccess: (response: T) => void, errorMessage: string): void {
    if (this.busyActions.has(key)) {
      return;
    }
    this.busyActions.add(key);
    action$.pipe(
      this.toastService.spinnerObservable(),
    ).subscribe({
      next: (response) => {
        this.busyActions.delete(key);
        onSuccess(response);
        this.loadData();
      },
      error: (error) => {
        this.busyActions.delete(key);
        this.toastService.error(error?.error?.message || errorMessage, 'Error');
        this.cdr.markForCheck();
      },
    });
  }

  privacyIcon(individual: AmlDashboardIndividual): CheckIcon {
    if (!individual.privacyStatus) {
      return { icon: 'mdi-help-circle-outline', styleClass: 'none', label: 'No consent form generated yet' };
    }
    // A signature past CONSENT_MAX_AGE_DAYS stays 'accepted' in the table but no longer counts.
    const agedOut = individual.privacyStatus === 'accepted' && !individual.privacyCompleted;
    const display = privacyConsentDisplayStatus({
      status: agedOut ? 'expired' : individual.privacyStatus,
      dispatchFailed: individual.privacyDispatchFailed,
      formType: individual.privacyFormType,
      signedTime: individual.privacySignedAt,
    });
    return { icon: display.icon, styleClass: display.styleKey, label: display.label };
  }

  kycIcon(individual: AmlDashboardIndividual): CheckIcon {
    return (individual.kycStatus && KYC_ICONS[individual.kycStatus]) || NOT_STARTED;
  }

  kybIcon(entity: AmlDashboardEntity): CheckIcon {
    if (!entity.kybStatus) {
      return NOT_STARTED;
    }
    const label = this.kybStatusLabel(entity.kybStatus);
    switch (kybDisplayStatus(entity.kybStatus)) {
      case 'resolved': return { icon: 'mdi-check-circle-outline', styleClass: 'ok', label };
      case 'failed': return { icon: 'mdi-alert-circle-outline', styleClass: 'error', label };
      default: return { icon: 'mdi-timer-sand', styleClass: 'waiting', label };
    }
  }

  privacyFailedCount(element: AmlDashboardApplication): number {
    return element.individuals
      .filter(individual => PRIVACY_FAILED_STYLES.includes(this.privacyIcon(individual).styleClass)).length;
  }

  kycFailedCount(element: AmlDashboardApplication): number {
    return element.individuals
      .filter(individual => individual.kycStatus === 'RECEIVED_FAILED' || individual.kycStatus === 'RECEIVED_ERROR').length;
  }

  kybFailedCount(element: AmlDashboardApplication): number {
    return element.entities
      .filter(entity => entity.kybStatus != null && kybDisplayStatus(entity.kybStatus) === 'failed').length;
  }

  hasIndividualActions(individual: AmlDashboardIndividual): boolean {
    return this.canResendPrivacy(individual) || this.canResendKycSms(individual);
  }

  /** Both checks done and nothing above Low, so the row needs no attention. */
  isPassed(individual: AmlDashboardIndividual): boolean {
    return individual.privacyCompleted && individual.kycCompleted && amlRiskRank(individual.riskLevel) <= 0;
  }

  partySummary(element: AmlDashboardApplication): string {
    const individuals = element.individuals.length;
    const entities = element.entities.length;
    return `${individuals} ${individuals === 1 ? 'individual' : 'individuals'} · ${entities} ${entities === 1 ? 'entity' : 'entities'}`;
  }

  riskText(riskLevel: string): string {
    const level = riskLevel.trim().toLowerCase();
    return level.charAt(0).toUpperCase() + level.slice(1);
  }

  riskClass(riskLevel: string): string {
    return `risk-${riskLevel.trim().toLowerCase()}`;
  }

  scoreText(score: number | null): string {
    return score == null ? '–' : String(score);
  }

  kybStatusLabel(status: KybResultStatus | null): string {
    if (!status) {
      return 'Not run';
    }
    return status.toLowerCase().split('_').map(word => word[0].toUpperCase() + word.slice(1)).join(' ');
  }

  businessNumber(entity: AmlDashboardEntity): string {
    if (entity.abn) {
      return `ABN ${entity.abn}`;
    }
    if (entity.acn) {
      return `ACN ${entity.acn}`;
    }
    return '-';
  }

  formatDay(value: string | null): string {
    return value ? moment(value).format('DD/MM/YYYY') : '-';
  }
}
